import {useCallback} from 'react';
import {useDispatch} from 'react-redux';

import isEmpty from 'lodash.isempty';
import {useCachedTable} from 'src/hooks/common-data-service/cached-table';
import {getEntitlementList} from 'src/modules/entitlements/utils/getEntitlementList';
import {getProductGrantsByIds} from 'src/openapi/admin/apis/ProductEntitlementsGrantApi';
import {getProductResourcesByIds} from 'src/openapi/admin/apis/ProductEntitlementsResourceApi';
import {getProductRolesByIds} from 'src/openapi/admin/apis/ProductEntitlementsRoleApi';
import {getProductUserEntitlementRelations} from 'src/openapi/admin/apis/ProductUserEntitlementsApi';
import {useAppDispatch} from 'src/store/hooks';

import {mcpUserEntitlementsStateSelector} from '../slices/mcp-user-entitlements-state.slice';
import {
    mcpUserEntitlementsActions,
    mcpUserEntitlementsDataSelector,
    mcpUserEntitlementsResultSelector,
} from '../slices/mcp-user-entitlements.slice';


import type {CacheKeys} from 'src/hooks/common-data-service/cached-table';
import type {EntitlementsFilters} from 'src/modules/entitlements/typedef';
import type {EntitlementResource} from 'src/openapi/admin/models/EntitlementResource';
import type {Grant} from 'src/openapi/admin/models/Grant';
import type {RelationPage} from 'src/openapi/admin/models/RelationPage';
import type {Role} from 'src/openapi/admin/models/Role';

type GetProductUserEntitlementsCacheKeys = CacheKeys<undefined, undefined, EntitlementsFilters>;

type Props = {
    userId: number,
};

export const useGetProductUserEntitlements = ({userId}: Props) => {
    const apiDispatch = useAppDispatch();
    const dispatch = useDispatch();

    const getAction = useCallback((cacheKeys: GetProductUserEntitlementsCacheKeys) => apiDispatch(
        getProductUserEntitlementRelations({
            userId,
            pageNumber: cacheKeys.pagination.currentPage,
            pageSize: cacheKeys.pagination.pageSize,
            sortDirection: cacheKeys.sortCriteria.direction,
            requestBody: cacheKeys.filters?.resourceIds as (string[] | undefined),
        }),
    ), [userId, apiDispatch]);

    const setLoadedData = useCallback(async (
        response: RelationPage,
        cacheKeys: GetProductUserEntitlementsCacheKeys,
    ) => {
        const filteredResources = cacheKeys.filters.resourceIds as string[] | undefined;
        const relations = filteredResources && !filteredResources.length
            ? [] : response.itemList || [];

        let roles: Record<string, Role> = {};
        const roleIds = [...new Set(relations
            .filter(({roleId}) => roleId)
            .map(({roleId}) => roleId || ''),
        )];
        const rolesResponse = await apiDispatch(getProductRolesByIds({requestBody: roleIds}));
        if (rolesResponse.payload.success) {
            roles = rolesResponse.payload.data;
        } else {
            dispatch(mcpUserEntitlementsActions.onFailure(rolesResponse.payload.data));
            return;
        }

        let grants: Record<string, Grant> = {};
        const grantIds = [...new Set(relations
            .map(({grantId}) => grantId),
        )];
        const grantsResponse = await apiDispatch(getProductGrantsByIds({requestBody: grantIds}));
        if (grantsResponse.payload.success) {
            grants = grantsResponse.payload.data;
        } else {
            dispatch(mcpUserEntitlementsActions.onFailure(grantsResponse.payload.data));
            return;
        }

        let resources: Record<string, EntitlementResource> = {};
        const resourceIds = [...new Set(relations
            .map(({resourceId}) => resourceId),
        )];
        const resourcesResponse = await apiDispatch(
            getProductResourcesByIds({requestBody: resourceIds}),
        );
        if (resourcesResponse.payload.success) {
            resources = resourcesResponse.payload.data;
        } else {
            dispatch(mcpUserEntitlementsActions.onFailure(resourcesResponse.payload.data));
            return;
        }

        const data = relations.length && !isEmpty(grants) && !isEmpty(resources)
            ? getEntitlementList({
                    roles,
                    grants,
                    resources,
                    relations,
                    sortDirection: cacheKeys.sortCriteria.direction,
                }) : [];

        dispatch(mcpUserEntitlementsActions.onSuccess({
            cacheKeys,
            totalCount: response.totalCount,
            data,
        }));
    }, [apiDispatch, dispatch]);

    return useCachedTable({
        dataSelector: mcpUserEntitlementsDataSelector,
        stateSelector: mcpUserEntitlementsStateSelector,
        resultSelector: mcpUserEntitlementsResultSelector,
        actions: mcpUserEntitlementsActions,
        getAction,
        onSuccessLoad: setLoadedData,
    });
};
